import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Badge, Card, Row, Col, Container, Spinner } from 'react-bootstrap';

import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { parsedUser } from '@/common/GetCurrentUser';
import APIService from '@/common/API';
import { FormatCurrency, userAvatar } from '@/common/helpers';
import AgentCustomers from './AgentCustomers';
import AgentPortfolioMap from './AgentPortfolioMap';
import AgentInvoicesByMonth from './AgentInvoicesByMonth';
import AgentActivity from './AgentActivity';
import { AgentOverview } from '@/types/Agents.types';

const VarianceBadge = ({ value }: { value?: number | null }) => {
    if (value === null || value === undefined) return <span className="text-muted">—</span>
    const isUp = value >= 0
    return (
        <span className={`fw-semibold ${isUp ? "text-success" : "text-danger"}`}>
            <i className={`fa fa-arrow-${isUp ? "up" : "down"} me-1`}></i>
            {Math.abs(value)}%
        </span>
    )
}

const AgentDetailsPage: React.FC = () => {
    const navigate = useHistory();
    const { id } = useParams<{ id: string }>();
    const { t } = useTranslation();
    const [overview, setOverview] = useState<AgentOverview | null>(null);
    const [loading, setLoading] = useState(true);
    const user = parsedUser();

    const bindData = useCallback(async () => {
        if (!user?.OrganizationId || !id) return
        try {
            setLoading(true);
            const response = await APIService.getAction(
                "agents/Overview",
                `OrganizationId=${user.OrganizationId}&Id=${id}`
            );
            if (response.data.status === "ok") {
                setOverview(response.data.response);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [user?.OrganizationId, id])

    useEffect(() => {
        bindData();
    }, [bindData]);

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    if (!overview) {
        return <div className="text-center text-danger py-5">{t("Agent not found")}</div>;
    }

    const { agent, places, visits, erp } = overview;

    return (
        <Container fluid className="px-4 py-3">
            <Card className="mb-4 shadow-sm border-0">
                <Card.Body className="p-4">
                    <Row className="align-items-center">
                        <Col xs={12} md={8} className="mb-3 mb-md-0">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                                <img
                                    src={agent.pic || userAvatar(`${agent.firstName} ${agent.lastName}`, 64)}
                                    alt={agent.firstName}
                                    style={{
                                        width: '4rem', height: '4rem', borderRadius: '50%', objectFit: 'cover',
                                        border: agent.color ? `3px solid ${agent.color}` : undefined
                                    }}
                                />
                                <div>
                                    <h1 style={{ fontSize: '1.875rem', fontWeight: '700', marginBottom: '0.25rem' }}>
                                        {agent.firstName} {agent.lastName}
                                    </h1>
                                    <p style={{ color: '#4b5563', margin: 0 }}>{agent.email}</p>
                                    <div className="mt-2">
                                        {agent.roleName && <Badge bg="primary" className="me-1">{agent.roleName}</Badge>}
                                        {agent.erpEnabled ? (
                                            <Badge bg="light" text="dark">
                                                {t("Seller")} #{agent.externalSellerId} — {agent.sellerName}
                                            </Badge>
                                        ) : (
                                            <Badge bg="light" text="muted">{t("No ERP link")}</Badge>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </Col>
                        <Col xs={12} md={4}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <i className="fa fa-calendar" style={{ color: '#3b82f6', minWidth: '1rem' }}></i>
                                    <span>{t("Joined")} {agent.created ? moment(agent.created).format('LL') : "—"}</span>
                                </div>
                                {agent.sellerPhone && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <i className="fa fa-phone" style={{ color: '#3b82f6', minWidth: '1rem' }}></i>
                                        <span>{agent.sellerPhone}</span>
                                    </div>
                                )}
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Performance Metrics */}
            <Row className="mb-4 g-3">
                <Col xs={12} md={6} lg={3}>
                    <Card className="h-100 shadow-sm border-0">
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-start">
                                <div>
                                    <p className="small text-muted mb-1">{t("Assigned Places")}</p>
                                    <p className="fs-3 fw-bold mb-0">{places.assigned}</p>
                                    <small className="text-muted">{places.linkedToErp} {t("in ERP")}</small>
                                </div>
                                <i className="fa fa-map-marker-alt fs-3 text-primary"></i>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col xs={12} md={6} lg={3}>
                    <Card className="h-100 shadow-sm border-0">
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-start">
                                <div>
                                    <p className="small text-muted mb-1">{t("Field Visit Coverage")}</p>
                                    <p className="fs-3 fw-bold mb-0">{visits.coveragePercent}%</p>
                                    <small className="text-muted">{visits.distinctPlacesVisited}/{places.assigned} {t("Places")}</small>
                                </div>
                                <i className="fa fa-check-circle fs-3 text-success"></i>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col xs={12} md={6} lg={3}>
                    <Card className="h-100 shadow-sm border-0">
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-start">
                                <div>
                                    <p className="small text-muted mb-1">{t("Customer Portfolio")}</p>
                                    <p className="fs-3 fw-bold mb-0">{erp.portfolioCustomers}</p>
                                    <small className="text-muted">{erp.portfolioWithPlace} {t("with place")}</small>
                                </div>
                                <i className="fa fa-address-book fs-3 text-purple" style={{ color: '#a855f7' }}></i>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col xs={12} md={6} lg={3}>
                    <Card className="h-100 shadow-sm border-0">
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-start">
                                <div>
                                    <p className="small text-muted mb-1">{t("Sales YTD")}</p>
                                    <p className="fs-3 fw-bold mb-0">{FormatCurrency(erp.salesYtd)}</p>
                                    <VarianceBadge value={erp.salesVariancePercent} />
                                </div>
                                <i className="fa fa-dollar-sign fs-3 text-warning"></i>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row className="mb-4 g-3">
                <Col xs={12} md={6} lg={3}>
                    <Card className="h-100 shadow-sm border-0">
                        <Card.Body>
                            <p className="small text-muted mb-1">{t("Visits")}</p>
                            <p className="fs-4 fw-bold mb-0">{visits.total}</p>
                            <small className="text-muted">{visits.thisMonth} {t("this month")} · {visits.last90Days} {t("last 90 days")}</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col xs={12} md={6} lg={3}>
                    <Card className="h-100 shadow-sm border-0">
                        <Card.Body>
                            <p className="small text-muted mb-1">{t("Invoices")}</p>
                            <p className="fs-4 fw-bold mb-0">{erp.invoiceCount}</p>
                            <small className="text-muted">{t("Average Ticket")}: {FormatCurrency(erp.avgTicket)}</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col xs={12} md={6} lg={3}>
                    <Card className="h-100 shadow-sm border-0">
                        <Card.Body>
                            <p className="small text-muted mb-1">{t("Quotes")}</p>
                            <p className="fs-4 fw-bold mb-0">{erp.quoteCount}</p>
                            <small className="text-muted">{t("Conversion Rate")}: {erp.conversionRate}%</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col xs={12} md={6} lg={3}>
                    <Card className="h-100 shadow-sm border-0">
                        <Card.Body>
                            <p className="small text-muted mb-1">{t("Unpaid Invoices")}</p>
                            <p className="fs-4 fw-bold mb-0 text-danger">{FormatCurrency(erp.unpaidAmount)}</p>
                            <small className="text-muted">{erp.unpaidCount} {t("Invoices")}</small>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {places.byStatus.length > 0 && (
                <Card className="mb-4 shadow-sm border-0">
                    <Card.Header className="bg-white border-bottom">
                        <h6 className="mb-0">
                            <i className="fa fa-map-marker-alt me-2 text-primary" />
                            {t("Assigned Places")} — {t("By Status")}
                        </h6>
                    </Card.Header>
                    <Card.Body className="d-flex flex-wrap gap-2">
                        {places.byStatus.map(status => (
                            <Badge
                                key={status.statusId}
                                bg="light"
                                text="dark"
                                className="px-3 py-2"
                                style={{ borderLeft: `4px solid ${status.color || '#6c757d'}` }}
                            >
                                {status.name}: {status.count}
                            </Badge>
                        ))}
                    </Card.Body>
                </Card>
            )}

            <AgentActivity agentId={id} userId={agent.userId} />

            <AgentPortfolioMap agentId={id} agentColor={agent.color} />

            <AgentInvoicesByMonth agentId={id} />

            <AgentCustomers ID={id} />
        </Container>
    );
};

export default AgentDetailsPage;
